import React from 'react';
import { StoryblokService } from '../../services/storyblok';
import { Props } from './types';
import { filterProps } from '../../helpers/filter-props';

type PodcastProvider = 'soundcloud' | 'spotify' | 'transistor';

type TrackTypeKeys = keyof TrackType;

type TrackType = {
  playlist?: (trackId: string) => string;
  track?: (trackId: string) => string;
  show?: (trackId: string) => string;
  episode?: (trackId: string) => string;
  artist?: (trackId: string) => string;
  album?: (trackId: string) => string;
  transistor?: (trackId: string) => string;
};

const embedHeight: Record<PodcastProvider, number> = {
  soundcloud: 200,
  spotify: 80,
  transistor: 185,
};

const brandColor = '1482fa';

const baseUrls: Record<PodcastProvider, string> = {
  soundcloud: 'https://w.soundcloud.com/player/?url=https://api.soundcloud.com',
  spotify: 'https://open.spotify.com/embed',
  transistor: 'https://share.transistor.fm/e',
};

const getSrc: Record<PodcastProvider, TrackType> = {
  soundcloud: {
    playlist: (trackId: string) => `${baseUrls.soundcloud}/playlists/${trackId}&color=%23${brandColor}`,
    track: (trackId: string) => `${baseUrls.soundcloud}/tracks/${trackId}&color=%23${brandColor}`,
  },
  spotify: {
    playlist: (trackId: string) => `${baseUrls.spotify}/playlist/${trackId}`,
    track: (trackId: string) => `${baseUrls.spotify}/track/${trackId}`,
    show: (trackId: string) => `${baseUrls.spotify}/show/${trackId}`,
    episode: (trackId: string) => `${baseUrls.spotify}/episode/${trackId}`,
    artist: (trackId: string) => `${baseUrls.spotify}/artist/${trackId}`,
    album: (trackId: string) => `${baseUrls.spotify}/album/${trackId}`,
  },
  transistor: {
    transistor: (trackId: string) => `${baseUrls.transistor}/${trackId}`,
  },
};

const getTrackType = (provider: PodcastProvider, trackUrl: string): TrackTypeKeys | null => {
  const providerTrackTypes = {
    spotify: ['playlist', 'track', 'show', 'episode', 'artist', 'album'],
    soundcloud: ['playlist', 'track'],
    transistor: ['transistor'],
  };

  const availableTypes = providerTrackTypes[provider] as TrackTypeKeys[];
  if (!availableTypes) {
    return null;
  }

  return availableTypes.find((availableType) => trackUrl?.includes(availableType)) || null;
};

// Regular expressions to extract track IDs from URLs of different podcast providers
const getTrackRegex = {
  spotify: /open\.spotify\.com\/embed\/(?:track|playlist|show|episode|artist|album)\/([^/?]+)/i,
  soundcloud: /api\.soundcloud\.com\/(?:tracks|playlists)\/([^/&]+)/i,
  transistor: /(?:share\.transistor\.fm\/e\/)([^/?]+)/i,
};

// This regular expression matches the URL value of an HTML iframe element's src property.
const iframeSrcRegex = /<iframe [^>]+src=["'](http[s]?:\/\/[^"']+)["']/i;

export const Podcast = ({ blok, slot }: Props) => {
  const { props } = filterProps(blok);
  const isEditor = StoryblokService.isInEditor();
  const formattedIFrameSnipped = (props.IframeSnippet as string).replaceAll('&quot;', '"');
  const iframeSrcAttribute = (formattedIFrameSnipped || '')?.match(iframeSrcRegex)?.[1] || '';
  if (!iframeSrcAttribute && isEditor) {
    return (
      <roche-editor-alert
        slot={slot}
        type='error'
        text='The embed snippet could not be parsed. Please review the embed code and try again.'
      />
    );
  }

  const providerName = Object.keys(embedHeight).find((provider) =>
    iframeSrcAttribute?.includes(provider),
  ) as PodcastProvider;
  if (!providerName && isEditor) {
    return (
      <roche-editor-alert
        slot={slot}
        type='warning'
        text='The provided embed markup belongs to an unsupported service.
        Only Spotify, Soundcloud, and Transistor are supported.'
      />
    );
  }

  const trackType = getTrackType(providerName, iframeSrcAttribute);

  if (!trackType) {
    return null;
  }

  const musicTrackId = iframeSrcAttribute?.match(getTrackRegex[providerName])?.[1];

  if (!musicTrackId) {
    return null;
  }

  return (
    <>
      {iframeSrcAttribute && providerName && trackType ? (
        <iframe
          title='Podcast Player'
          src={getSrc[providerName]?.[trackType]?.(musicTrackId)}
          height={embedHeight[providerName]}
          width='100%'
          frameBorder='0'
          seamless
          allow='autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture'
          allowFullScreen
          loading='lazy'
          slot={slot}
        />
      ) : null}
    </>
  );
};
